import { createSelector } from '@reduxjs/toolkit';
import { errorToast, toNumericId } from '@/helpers';
import { _notNil } from '@/littledash';
import { ApiService } from '@/support/ApiService';
import { featuresSelector } from '@/support/Selectors';
import type { Animal } from '@/model/Animal.model';
import type { FC } from 'react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { modalAction } from '@/utils/modal';

const reasonForChangeSelector = createSelector([featuresSelector], (features) => ({
  reasonForChangeEnabled: features?.reason_for_change === true,
}));

interface RemoveFromGroupProps {
  subject: Animal | Array<Animal>;
  handleCallback: (text?: string) => void;
  closeModal: () => void;
}

/**
 * Confirmation modal for removing one or more animals from their study group.
 */
const RemoveFromGroup: FC<RemoveFromGroupProps> = ({ subject, handleCallback, closeModal }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { reasonForChangeEnabled } = useSelector(reasonForChangeSelector);
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);

  const subjects = Array.isArray(subject) ? subject : [subject];
  const isMultiple = subjects.length > 1;
  const what = isMultiple ? 'these animals' : 'this animal';
  const where = isMultiple ? 'their groups' : 'its group';

  const removeFromGroup = async (reasonForChange?: string) => {
    setIsLoading(true);

    try {
      const animals = subjects.map((s) => ({
        id: toNumericId(s.id),
        study_group_id: null,
      }));

      await ApiService.call({
        endpoint: 'PATCH /api/v1/animals',
        body: {
          animals,
          ...(_notNil(reasonForChange) ? { reason_for_change: reasonForChange } : {}),
        },
        options: { onError: { throw: true, toast: false, slug: 'remove-from-group-bulk-update' } },
      });

      handleCallback('Successfully removed from group');
    } catch {
      if (reasonForChangeEnabled) {
        // The reason-for-change modal has replaced this modal, so the inline error is
        // unmounted; surface the failure via a toast that survives the unmount.
        errorToast('Unable to remove from group. Please try again.');
      } else {
        setError('Unable to remove from group. Please try again.');
      }
      setIsLoading(false);
    }
  };

  const onSubmit = (event: React.MouseEvent) => {
    event.preventDefault();
    if (reasonForChangeEnabled) {
      openModal('BULK_REASON_FOR_CHANGE', {
        title: 'Animal information has changed',
        onClick: (reasonForChange?: string) => removeFromGroup(reasonForChange),
      });
      return;
    }
    void removeFromGroup();
  };

  return (
    <form className={`ui__narrow-modal ${isLoading && 'disabled-form'}`}>
      <div className="pb3 tc">
        <h3 className="normal f4 pb2">
          Are you sure you want to remove {what} from {where}?
        </h3>
      </div>
      {error && <p className="red">{error}</p>}
      <div>
        <button type="submit" onClick={onSubmit} className="button w-100" data-testid="remove-from-group__submit">
          Remove from group
        </button>
        <button type="button" className="button plain db w-100 mt2" onClick={closeModal}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default RemoveFromGroup;
